<template>
  <div class="flex flex-col h-full">
    <!-- ── Header (fondo oscuro) ── -->
    <div class="relative px-5 py-4 flex-shrink-0 overflow-hidden bg-gray-950">
      <div class="absolute inset-0 opacity-[0.04]" style="background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')"></div>

      <div class="flex items-center justify-between relative z-10">
        <div>
          <h3 class="text-base font-bold text-white tracking-tight flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-4 w-4 text-indigo-400">
              <path fill-rule="evenodd" d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5Z" clip-rule="evenodd" />
            </svg>
            {{ t('assistant.title') }}
          </h3>
          <p class="text-gray-400 text-xs mt-0.5">
            {{ greeting }}
          </p>
        </div>
        <button
          @click="$emit('close')"
          class="text-gray-500 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          :aria-label="t('common.close')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- ── Contenido con scroll ── -->
    <div class="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom,0px)] sm:pb-0">
      <!-- Estado de carga -->
      <div v-if="isLoading" class="flex flex-col items-center justify-center py-12">
        <svg class="h-6 w-6 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <p class="text-xs text-gray-400 mt-2">{{ t('assistant.loading') }}</p>
      </div>

      <!-- ═══ MINI-FORM INCIDENCIA ═══ -->
      <div v-else-if="showIncidentForm" class="p-4">
        <div class="flex items-center gap-2 mb-4">
          <button @click="closeIncidentForm" class="text-gray-400 hover:text-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h4 class="text-sm font-bold text-gray-900">{{ t('assistant.incidentTitle') }}</h4>
        </div>

        <!-- Seleccionar coliving (si tiene varios) -->
        <div v-if="activeStaysWithChat.length > 1" class="mb-3">
          <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('assistant.incidentColiving') }}</label>
          <select
            v-model="incidentForm.chatId"
            class="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-colors"
          >
            <option value="">{{ t('assistant.incidentSelectColiving') }}</option>
            <option v-for="stay in activeStaysWithChat" :key="stay.chatId" :value="stay.chatId">
              {{ stay.title }}
            </option>
          </select>
        </div>

        <!-- Tipo de incidencia -->
        <div class="mb-3">
          <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('assistant.incidentType') }}</label>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="iType in incidentTypes"
              :key="iType.value"
              @click="incidentForm.type = iType.value"
              class="px-3 py-2 text-xs font-medium rounded-xl border transition-all duration-200"
              :class="incidentForm.type === iType.value
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'"
            >
              {{ iType.label }}
            </button>
          </div>
        </div>

        <!-- Descripción -->
        <div class="mb-3">
          <label class="block text-xs font-medium text-gray-600 mb-1">{{ t('assistant.incidentDescription') }}</label>
          <textarea
            v-model="incidentForm.description"
            rows="3"
            maxlength="500"
            class="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 resize-none transition-colors"
            :placeholder="t('assistant.incidentPlaceholder')"
          ></textarea>
          <p class="text-[10px] text-gray-400 text-right mt-0.5">{{ incidentForm.description.length }}/500</p>
        </div>

        <!-- Enviar -->
        <button
          @click="submitIncident"
          :disabled="!canSubmitIncident || isSubmittingIncident"
          class="w-full px-4 py-2.5 rounded-full text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          <span v-if="isSubmittingIncident" class="inline-flex items-center gap-2">
            <svg class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            {{ t('assistant.incidentSending') }}
          </span>
          <span v-else>{{ t('assistant.incidentSubmit') }}</span>
        </button>

        <!-- Éxito -->
        <div v-if="incidentSuccess" class="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
          <p class="text-xs text-emerald-700 font-medium">{{ t('assistant.incidentSuccess') }}</p>
        </div>
      </div>

      <!-- ═══ CONTENIDO PRINCIPAL: ALERTAS + SECCIONES ═══ -->
      <div v-else>
        <!-- ═══ RESUMEN DE ESTANCIAS ACTIVAS ═══ -->
        <div v-if="activeStaySummaries.length > 0" class="px-3 pt-3 space-y-2">
          <div
            v-for="stay in activeStaySummaries"
            :key="stay.id"
            class="border border-gray-200 rounded-xl overflow-hidden bg-white"
          >
            <div class="px-3 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <h4 class="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {{ stay.title }}
              </h4>
              <button @click="toggleStaySummary(stay.id)" class="text-gray-400 hover:text-gray-600 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transition-transform" :class="expandedStays[stay.id] ? 'rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            <Transition name="slide">
              <div v-if="expandedStays[stay.id]" class="px-3 py-2.5 space-y-1.5 text-xs text-gray-600">
                <div v-if="stay.room" class="flex items-center gap-2">
                  <span class="text-gray-400 w-20 flex-shrink-0">Habitación</span>
                  <span class="font-medium text-gray-800">{{ stay.room }}</span>
                </div>
                <div v-if="stay.address" class="flex items-center gap-2">
                  <span class="text-gray-400 w-20 flex-shrink-0">Dirección</span>
                  <span class="font-medium text-gray-800 truncate">{{ stay.address }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-gray-400 w-20 flex-shrink-0">Estancia</span>
                  <span class="font-medium text-gray-800">{{ stay.startDate }} → {{ stay.endDate }}</span>
                </div>
                <div v-if="stay.price" class="flex items-center gap-2">
                  <span class="text-gray-400 w-20 flex-shrink-0">Renta</span>
                  <span class="font-medium text-gray-800">{{ stay.price }}€/mes</span>
                </div>
                <div v-if="stay.agent" class="flex items-center gap-2">
                  <span class="text-gray-400 w-20 flex-shrink-0">Agente</span>
                  <span class="font-medium text-gray-800">{{ stay.agent }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-gray-400 w-20 flex-shrink-0">Días rest.</span>
                  <span class="font-semibold" :class="stay.daysLeft <= 7 ? 'text-red-600' : stay.daysLeft <= 30 ? 'text-amber-600' : 'text-emerald-600'">
                    {{ stay.daysLeft > 0 ? stay.daysLeft + ' días' : 'Finalizada' }}
                  </span>
                </div>

                <!-- Mini historial de pagos -->
                <div v-if="stay.payments && stay.payments.length > 0" class="pt-1.5 mt-1.5 border-t border-gray-100">
                  <p class="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Pagos</p>
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="pay in stay.payments"
                      :key="pay.id"
                      class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-medium"
                      :class="pay.paid ? 'bg-emerald-50 text-emerald-700' : pay.overdue ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-500'"
                    >
                      <svg v-if="pay.paid" xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      <svg v-else-if="pay.overdue" xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span v-else class="w-2.5 h-2.5 rounded-full border border-gray-300"></span>
                      {{ pay.label }}
                    </span>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>

        <!-- ═══ CARRUSEL HORIZONTAL: Perfil / Docs / Avalista / Onboarding ═══ -->
        <div v-if="carouselAlerts.length > 0" class="pt-3 px-3">
          <div class="flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-1" style="-ms-overflow-style: none; scrollbar-width: none;">
            <div
              v-for="alert in carouselAlerts"
              :key="alert.id"
              class="flex-shrink-0 w-[200px] snap-start p-3 rounded-xl border transition-all duration-200 cursor-pointer group"
              :class="[getAlertCardClass(alert)]"
              @click="alert.action ? handleNavigate(alert) : handleDismiss(alert)"
            >
              <div class="flex items-center gap-2 mb-1.5">
                <div
                  class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  :class="getIconBgClass(alert)"
                >
                  <!-- User -->
                  <svg v-if="alert.icon === 'user'" xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <!-- Doc -->
                  <svg v-else-if="alert.icon === 'doc'" xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <!-- Shield -->
                  <svg v-else-if="alert.icon === 'shield'" xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <!-- Star -->
                  <svg v-else-if="alert.icon === 'star'" xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  <!-- Home -->
                  <svg v-else-if="alert.icon === 'home'" xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <!-- Default -->
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p class="text-xs font-semibold text-gray-900 leading-tight truncate">{{ alert.title }}</p>
              </div>
              <p class="text-[11px] text-gray-500 leading-relaxed line-clamp-2">{{ alert.message }}</p>
              <div class="flex items-center justify-between mt-2">
                <button
                  @click.stop="handleDismiss(alert)"
                  class="text-[10px] font-medium text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-0.5"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  {{ t('assistant.markSeen') }}
                </button>
                <span v-if="alert.action" class="text-[10px] font-semibold text-indigo-500 group-hover:text-indigo-700 flex items-center gap-0.5 transition-colors">
                  {{ alert.type === 'onboarding' && alert.icon === 'home' ? t('assistant.searchColivings') : alert.type === 'onboarding' ? t('assistant.startStep') : t('assistant.goTo') }}
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ═══ ALERTAS DE ACCIÓN (verticales) ═══ -->
        <div v-if="actionAlerts.length > 0" class="p-3 space-y-2">
          <div
            v-for="alert in actionAlerts"
            :key="alert.id"
            class="p-3 rounded-xl border transition-all duration-200"
            :class="[getAlertCardClass(alert)]"
          >
            <div class="flex gap-3">
              <!-- Icono -->
              <div
                class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                :class="getIconBgClass(alert)"
              >
                <!-- Clock -->
                <svg v-if="alert.icon === 'clock'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <!-- Calendar -->
                <svg v-else-if="alert.icon === 'calendar'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <!-- Payment -->
                <svg v-else-if="alert.icon === 'payment'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <!-- Pen / Sign -->
                <svg v-else-if="alert.icon === 'pen'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <!-- Chat -->
                <svg v-else-if="alert.icon === 'chat'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <!-- Refresh / Renewal -->
                <svg v-else-if="alert.icon === 'refresh'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <!-- Wrench / Incident -->
                <svg v-else-if="alert.icon === 'wrench'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <!-- Home / Suggestions -->
                <svg v-else-if="alert.icon === 'home'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <!-- Info (default) -->
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" :class="getIconColorClass(alert)" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <!-- Contenido -->
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-2">
                  <p class="text-sm font-semibold text-gray-900 leading-tight">{{ alert.title }}</p>
                  <span v-if="alert.priority <= 1" class="flex-shrink-0 w-2 h-2 rounded-full mt-1.5" :class="alert.priority === 0 ? 'bg-red-500' : 'bg-amber-500'"></span>
                </div>
                <p class="text-xs text-gray-500 mt-0.5 leading-relaxed">{{ alert.message }}</p>
                <p v-if="alert.contractTitle" class="text-xs text-gray-700 font-medium mt-1 truncate">{{ alert.contractTitle }}</p>

                <!-- Acciones por tipo -->
                <div class="flex items-center gap-2 mt-2 flex-wrap">
                  <!-- Visto (oculta la alerta) -->
                  <button
                    @click="handleDismiss(alert)"
                    class="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {{ t('assistant.markSeen') }}
                  </button>

                  <!-- Acción: Renovar -->
                  <button
                    v-if="alert.action?.type === 'renewal'"
                    @click="handleRenewalAction(alert)"
                    class="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-600 hover:text-teal-800 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {{ t('assistant.renew') }}
                  </button>

                  <!-- Acción: Pagar -->
                  <button
                    v-if="alert.action?.type === 'payment'"
                    @click="handlePaymentAction(alert)"
                    class="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-800 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {{ t('assistant.pay') }}
                  </button>

                  <!-- Acción: Reportar incidencia -->
                  <button
                    v-if="alert.action?.type === 'report-incident'"
                    @click="openIncidentForm(alert.action.chatId)"
                    class="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-600 hover:text-orange-800 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {{ t('assistant.reportIncident') }}
                  </button>

                  <!-- Acción: Solicitud sin respuesta (ver + retirar) -->
                  <button
                    v-if="alert.action?.type === 'withdraw-or-view'"
                    @click="handleNavigate({ ...alert, action: { type: 'navigate', path: `/colivings/${alert.action.colivingId}` } })"
                    class="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                  >
                    {{ t('assistant.goTo') }}
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    v-if="alert.action?.type === 'withdraw-or-view'"
                    @click="handleWithdrawApplication(alert)"
                    class="inline-flex items-center gap-1 text-[11px] font-semibold text-red-500 hover:text-red-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {{ t('assistant.withdraw') }}
                  </button>

                  <!-- Acción: Ver recomendaciones -->
                  <button
                    v-if="alert.action?.type === 'view-recommendations'"
                    @click="loadAndShowRecommendations"
                    class="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {{ t('assistant.viewRecommendations') }}
                  </button>

                  <!-- Acción genérica: Ver / Empezar / Buscar -->
                  <button
                    v-if="alert.action?.type === 'navigate' || alert.action?.type === 'open-chat'"
                    @click="handleNavigate(alert)"
                    class="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                  >
                    {{ alert.type === 'sepa'
                        ? t('assistant.completeSepa')
                        : t('assistant.goTo') }}
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ═══ RECOMENDACIONES DE COLIVINGS ═══ -->
        <div v-if="showRecommendations && recommendations.length > 0" class="px-3 pb-3">
          <div class="border border-indigo-100 rounded-xl overflow-hidden">
            <div class="px-3 py-2 bg-indigo-50/50 flex items-center justify-between">
              <h4 class="text-xs font-bold text-indigo-900 flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {{ t('assistant.recommendationsTitle') }}
              </h4>
              <button @click="showRecommendations = false" class="text-gray-400 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div class="divide-y divide-gray-100">
              <div
                v-for="rec in recommendations"
                :key="rec.id"
                class="px-3 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                @click="goToColiving(rec)"
              >
                <div class="flex gap-2.5">
                  <div class="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                    <img
                      v-if="rec.images && rec.images.length > 0"
                      :src="rec.images[0]"
                      :alt="rec.title"
                      class="w-full h-full object-cover"
                    />
                    <div v-else class="w-full h-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-semibold text-gray-900 truncate">{{ rec.title }}</p>
                    <p class="text-[10px] text-gray-500 truncate">{{ rec.propertyLocation }}</p>
                    <div class="flex items-center gap-2 mt-0.5">
                      <span class="text-xs font-bold text-gray-900">{{ rec.cheapestRoomPrice || rec.basePrice }}€<span class="text-[10px] font-normal text-gray-400">/mes</span></span>
                      <span v-if="rec.matchData?.score" class="text-[10px] font-medium text-indigo-600">{{ rec.matchData.score }}% match</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ver más -->
            <div class="px-3 py-2 bg-gray-50 border-t border-gray-100">
              <router-link
                to="/encuentratucoliving"
                class="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                @click="$emit('close')"
              >
                {{ t('assistant.viewMoreColivings') }} →
              </router-link>
            </div>
          </div>
        </div>

        <!-- Cargando recomendaciones -->
        <div v-if="isLoadingRecommendations" class="px-3 pb-3">
          <div class="flex items-center justify-center py-6 border border-gray-100 rounded-xl">
            <svg class="h-4 w-4 animate-spin text-gray-400 mr-2" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <span class="text-xs text-gray-400">{{ t('assistant.loadingRecommendations') }}</span>
          </div>
        </div>

        <!-- Sin alertas: todo bien -->
        <div v-if="!alerts.length && !isLoading" class="flex flex-col items-center justify-center py-12 px-6 text-center">
          <div class="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p class="text-sm font-semibold text-gray-900">{{ t('assistant.allGood') }}</p>
          <p class="text-xs text-gray-400 mt-1">{{ t('assistant.allGoodHint') }}</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { subscribeToNotifications, markNotificationAsRead } from '@/services/notificationService';
import { isAffiliatePartnerListing, openAffiliatePartnerListing, openExternalColivingListing } from '@/utils/externalRedirect';
import {
  generateAssistantAlerts,
  countUrgentAlerts,
  getActiveStaysWithChat,
} from '@/utils/assistantAlerts';

const { t } = useI18n();
const router = useRouter();
const emit = defineEmits(['close', 'urgent-count']);

// ── Estado general ──
const isLoading = ref(true);
const alerts = ref([]);
const currentUser = ref(null);
const userDisplayName = ref('');

// ── Persistencia de alertas descartadas ──
const DISMISSED_STORAGE_KEY = 'assistant_dismissed_alerts';

function loadDismissedFromStorage() {
  try {
    const stored = localStorage.getItem(DISMISSED_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Limpiar entradas viejas (> 7 días) para no acumular indefinidamente
      const now = Date.now();
      const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
      const valid = Object.fromEntries(
        Object.entries(parsed).filter(([, ts]) => now - ts < MAX_AGE)
      );
      return new Map(Object.entries(valid));
    }
  } catch (e) {
    console.warn('Error loading dismissed alerts:', e);
  }
  return new Map();
}

function saveDismissedToStorage(map) {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(Object.fromEntries(map)));
  } catch (e) {
    console.warn('Error saving dismissed alerts:', e);
  }
}

// Map<alertId, timestamp> en lugar de Set para poder expirar
const dismissedAlerts = ref(loadDismissedFromStorage());

// ── Datos fuente ──
const contracts = ref([]);
const notifications = ref([]);
const chats = ref([]);
const profileData = ref(null);
const renewalStatuses = ref({}); // { colivingId: { hasPendingRenewal, ... } }
const newRecommendationsCount = ref(0); // Nuevas recomendaciones desde la última visita
const expandedStays = ref({}); // { [colivingId]: boolean } — controla qué tarjetas están expandidas

function toggleStaySummary(stayId) {
  expandedStays.value[stayId] = !expandedStays.value[stayId];
}

// ── Suscripciones ──
let authUnsubscribe = null;
let notifUnsubscribe = null;
let chatsUnsubscribe = null;

// ── Incidencia (mini-form) ──
const showIncidentForm = ref(false);
const isSubmittingIncident = ref(false);
const incidentSuccess = ref(false);
const incidentForm = ref({
  chatId: '',
  type: '',
  description: '',
});

const incidentTypes = [
  { value: 'averia', label: 'Avería' },
  { value: 'rotura', label: 'Rotura' },
  { value: 'servicio', label: 'Servicio' },
  { value: 'otros', label: 'Otros' },
];

const activeStaysWithChat = computed(() => getActiveStaysWithChat(contracts.value, chats.value));

/**
 * Computed: resumen de TODAS las estancias activas (room_assigned).
 * Incluye info de la habitación, fechas, precio, agente y estado de pagos.
 */
const activeStaySummaries = computed(() => {
  const activeContracts = contracts.value.filter(
    (c) => c.type === 'coliving' && c.applicationStatus === 'room_assigned'
  );
  if (!activeContracts.length) return [];

  const now = new Date();
  const formatDate = (d) => {
    if (!d) return '—';
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return activeContracts.map((active) => {
    const appData = active.applicationData || {};

    // Fechas
    const startRaw = appData.desiredStartDate || active.desiredStartDate;
    const endRaw = active.endDate || active.desiredEndDate || appData.calculatedEndDate || appData.desiredEndDate;
    const startDate = startRaw ? new Date(typeof startRaw === 'string' ? startRaw : startRaw.toDate ? startRaw.toDate() : startRaw) : null;
    const endDate = endRaw ? new Date(typeof endRaw === 'string' ? endRaw : endRaw.toDate ? endRaw.toDate() : endRaw) : null;

    const daysLeft = endDate ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : 0;

    // Habitación
    const room = active.assignedRoom || appData.assignedRoomData;
    const roomLabel = room ? `Hab. ${room.roomNumber || room.name || ''}${room.price ? ` (${room.price}€)` : ''}` : '';

    // Precio
    const price = room?.price || active.propertyPrice || active.monthlyRent || appData.monthlyRent || 0;

    // Pagos
    const stayDuration = appData.stayDurationMonths || active.stayDurationMonths || 0;
    const paymentConfirmations = appData.paymentConfirmations || [];
    const payments = [];
    for (let m = 1; m <= stayDuration; m++) {
      const paymentId = m <= 1 ? 'initial' : `month-${m}`;
      const paid = paymentConfirmations.some((p) => p.paymentId === paymentId && p.confirmed === true);
      let overdue = false;
      if (!paid && startDate) {
        const monthDate = new Date(startDate);
        monthDate.setMonth(monthDate.getMonth() + (m - 1));
        monthDate.setDate(5);
        overdue = now > monthDate;
      }
      payments.push({
        id: paymentId,
        label: m === 1 ? 'Inicial' : `M${m}`,
        paid,
        overdue,
      });
    }

    return {
      id: active.colivingId || active.id,
      title: active.title || 'Tu coliving',
      room: roomLabel,
      address: active.propertyAddress || active.propertyLocation || '',
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      daysLeft,
      price,
      agent: active.agentName || '',
      payments,
    };
  });
});

const canSubmitIncident = computed(() => {
  const chatOk = activeStaysWithChat.value.length === 1 || incidentForm.value.chatId;
  return chatOk && incidentForm.value.type && incidentForm.value.description.trim().length > 0;
});

// ── Alertas visibles (excluye las marcadas como "visto") ──
const visibleAlerts = computed(() => {
  return alerts.value.filter((a) => !dismissedAlerts.value.has(a.id));
});

// Tipos de alerta que van en el carrusel horizontal (estado de perfil / onboarding)
const CAROUSEL_TYPES = new Set(['profile', 'documents', 'guarantor', 'onboarding']);

const carouselAlerts = computed(() => {
  return visibleAlerts.value.filter((a) => CAROUSEL_TYPES.has(a.type));
});

const actionAlerts = computed(() => {
  return visibleAlerts.value.filter((a) => !CAROUSEL_TYPES.has(a.type));
});

// ── Recomendaciones ──
const showRecommendations = ref(false);
const isLoadingRecommendations = ref(false);
const recommendations = ref([]);

// ── Greeting personalizado ──
const greeting = computed(() => {
  if (userDisplayName.value) {
    return t('assistant.greeting', { name: userDisplayName.value });
  }
  return t('assistant.greetingGeneric');
});

// ══════════════════════════════════════════════════
// CICLO DE VIDA
// ══════════════════════════════════════════════════

onMounted(() => {
  const auth = getAuth();
  authUnsubscribe = onAuthStateChanged(auth, async (user) => {
    currentUser.value = user;
    if (user) {
      userDisplayName.value = user.displayName ? user.displayName.split(' ')[0] : '';
      await loadAllData(user.uid);
    } else {
      resetState();
    }
  });
});

onUnmounted(() => {
  if (authUnsubscribe) authUnsubscribe();
  if (notifUnsubscribe) notifUnsubscribe();
  if (chatsUnsubscribe) chatsUnsubscribe();
});

// ══════════════════════════════════════════════════
// CARGA DE DATOS
// ══════════════════════════════════════════════════

async function loadAllData(uid) {
  isLoading.value = true;

  try {
    // Cargar todo en paralelo
    await Promise.allSettled([
      loadContracts(uid),
      loadProfileData(uid),
    ]);

    // Cargar estados de renovación para estancias activas
    await loadRenewalStatuses();

    // Comprobar nuevas recomendaciones de colivings
    await checkNewRecommendations(uid);

    // Suscripciones en tiempo real
    subscribeNotifications();
    subscribeChats(uid);

    // Generar alertas iniciales
    rebuildAlerts();
  } catch (error) {
    console.error('Error loading assistant data:', error);
  } finally {
    isLoading.value = false;
  }
}

async function loadContracts(uid) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const token = await user.getIdToken();
    const response = await fetch('/api/contracts/user-applications', {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (response.ok) {
      contracts.value = await response.json();
    }
  } catch (error) {
    console.error('Error loading contracts:', error);
  }
}

async function loadProfileData(uid) {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (!userDoc.exists()) return;

    const data = userDoc.data();
    const hasDescription = !!(data.applicantDescription && data.applicantDescription.trim());
    const hasIncome = !!(data.monthlyIncome && data.monthlyIncome > 0);
    const hasGuarantor = !!data.hasGuarantor;
    const guarantorInfo = data.guarantorInfo || {};

    // Calcular progreso simplificado
    let docsCompleted = 0;
    const totalDocs = 5;
    try {
      const savedDocs = localStorage.getItem('essentialDocuments');
      if (savedDocs) {
        const parsed = JSON.parse(savedDocs);
        docsCompleted = Object.keys(parsed).filter((k) => parsed[k]?.length > 0).length;
      }
    } catch (e) { /* ignorar */ }

    const documentsProgress = Math.round((docsCompleted / totalDocs) * 100);
    const profileProgress = ((hasDescription ? 1 : 0) + (hasIncome ? 1 : 0)) / 2 * 100;
    const guarantorProgress = !hasGuarantor ? 0
      : !guarantorInfo.fullName ? 25
      : guarantorInfo.status === 'rejected' ? 50
      : guarantorInfo.status === 'verified' ? 100
      : 75;

    const overallProgress = Math.round(
      documentsProgress * 0.5 + profileProgress * 0.3 + guarantorProgress * 0.2
    );

    profileData.value = {
      overallProgress,
      hasDescription,
      hasIncome,
      hasGuarantor,
      guarantorInfo,
      documentsProgress,
    };
  } catch (error) {
    console.error('Error loading profile data:', error);
  }
}

/**
 * Carga el estado de renovación de cada estancia activa (room_assigned).
 * Evita mostrar la alerta de "renovar" si ya hay una solicitud pendiente.
 */
async function loadRenewalStatuses() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const token = await user.getIdToken();
    const activeColivings = contracts.value.filter(
      (c) => c.type === 'coliving' && c.applicationStatus === 'room_assigned'
    );

    const statuses = {};
    await Promise.allSettled(
      activeColivings.map(async (contract) => {
        const colivingId = contract.colivingId || contract.id;
        try {
          const res = await fetch(`/api/colivings/${colivingId}/renewal-status`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            statuses[colivingId] = {
              hasPendingRenewal: data.hasRenewal && data.status === 'pending',
              renewalStatus: data.status || null,
            };
          }
        } catch (e) {
          console.warn(`Error fetching renewal status for ${colivingId}:`, e);
        }
      })
    );

    renewalStatuses.value = statuses;
  } catch (error) {
    console.warn('Error loading renewal statuses:', error);
  }
}

/**
 * Comprueba si hay nuevos colivings recomendados desde la última visita.
 * Usa localStorage para recordar cuándo se hizo la última comprobación.
 */
async function checkNewRecommendations(uid) {
  try {
    const lastCheckKey = `assistant_recs_last_check_${uid}`;
    const lastCheck = localStorage.getItem(lastCheckKey);
    // Por defecto, últimos 7 días si nunca se ha comprobado
    const since = lastCheck || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    const res = await fetch(`/api/colivings/recommendations/new?userId=${uid}&since=${encodeURIComponent(since)}`);
    if (res.ok) {
      const data = await res.json();
      newRecommendationsCount.value = data.count || data.recommendations?.length || 0;
    }

    // Actualizar marca temporal
    localStorage.setItem(lastCheckKey, new Date().toISOString());
  } catch (error) {
    console.warn('[Assistant] Error checking new recommendations:', error);
  }
}

function subscribeNotifications() {
  if (notifUnsubscribe) notifUnsubscribe();
  notifUnsubscribe = subscribeToNotifications((newNotifs) => {
    notifications.value = newNotifs;
    rebuildAlerts();
  }, 10);
}

function subscribeChats(uid) {
  if (chatsUnsubscribe) chatsUnsubscribe();
  try {
    // Suscripción a chats donde el usuario es el inquilino
    const chatsQuery = query(
      collection(db, 'chats'),
      where('participants.tenantId', '==', uid),
      where('status', '==', 'active')
    );

    chatsUnsubscribe = onSnapshot(chatsQuery, (snapshot) => {
      chats.value = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        unreadCount: d.data().unreadCount?.[uid] || 0,
        contactName: d.data().participants?.agentName || '',
      }));
      rebuildAlerts();
    }, (error) => {
      console.warn('Error subscribing to chats:', error);
    });
  } catch (error) {
    console.warn('Error setting up chats subscription:', error);
  }
}

function resetState() {
  contracts.value = [];
  notifications.value = [];
  chats.value = [];
  profileData.value = null;
  alerts.value = [];
  isLoading.value = false;
}

// ══════════════════════════════════════════════════
// RECONSTRUIR ALERTAS
// ══════════════════════════════════════════════════

function rebuildAlerts() {
  const generated = generateAssistantAlerts({
    contracts: contracts.value,
    currentUserId: currentUser.value?.uid || '',
    notifications: notifications.value,
    chats: chats.value,
    profileData: profileData.value,
    renewalStatuses: renewalStatuses.value,
    newRecommendationsCount: newRecommendationsCount.value,
  });

  alerts.value = generated;
  emit('urgent-count', countUrgentAlerts(generated));
}

// ══════════════════════════════════════════════════
// ACCIONES GENERALES
// ══════════════════════════════════════════════════

function dismissAlert(alertId, notificationId) {
  dismissedAlerts.value.set(alertId, Date.now());
  saveDismissedToStorage(dismissedAlerts.value);
  if (notificationId) {
    markNotificationAsRead(notificationId).catch((e) => { console.warn('Error marking notification as read:', e); });
  }
}

function handleDismiss(alert) {
  dismissAlert(alert.id, alert.notificationId);
}

function handleNavigate(alert) {
  if (!alert.action) return;
  // Marcar como vista al navegar
  dismissAlert(alert.id, alert.notificationId);

  if (alert.action.type === 'navigate') {
    emit('close');
    router.push(alert.action.path);
  } else if (alert.action.type === 'open-chat') {
    emit('close');
    // Disparar evento global para que ChatNavbarButton abra el panel
    window.dispatchEvent(new CustomEvent('open-chat-panel'));
  }
}

// ══════════════════════════════════════════════════
// ACCIÓN: RENOVACIÓN
// ══════════════════════════════════════════════════

function handleRenewalAction(alert) {
  dismissAlert(alert.id, alert.notificationId);
  emit('close');
  // Navegar al coliving donde está el formulario de renovación
  const colivingId = alert.action?.colivingId;
  if (colivingId) {
    router.push(`/colivings/${colivingId}`);
  }
}

// ══════════════════════════════════════════════════
// ACCIÓN: PAGO
// ══════════════════════════════════════════════════

function handlePaymentAction(alert) {
  dismissAlert(alert.id, alert.notificationId);
  emit('close');
  const path = alert.action?.path;
  if (path) {
    router.push(path);
  }
}

// ══════════════════════════════════════════════════
// ACCIÓN: RETIRAR SOLICITUD
// ══════════════════════════════════════════════════

async function handleWithdrawApplication(alert) {
  const colivingId = alert.action?.colivingId;
  if (!colivingId) return;

  // Confirmar con el usuario
  if (!window.confirm(t('assistant.withdrawConfirm', { title: alert.action.colivingTitle || 'este coliving' }))) {
    return;
  }

  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const token = await user.getIdToken();
    const response = await fetch(`/api/colivings/${colivingId}/my-application`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (response.ok) {
      // Eliminar el contrato retirado de la lista local para que no se regenere la alerta
      const appId = alert.action?.applicationId;
      if (appId) {
        contracts.value = contracts.value.filter((c) => c.applicationId !== appId);
      } else {
        // Fallback: filtrar por colivingId + estado
        contracts.value = contracts.value.filter(
          (c) => !(c.colivingId === colivingId && (c.applicationStatus === 'submitted' || c.applicationStatus === 'under_review'))
        );
      }
      dismissAlert(alert.id, alert.notificationId);
      rebuildAlerts();
    } else {
      const errorData = await response.json().catch(() => ({}));
      console.warn('[Assistant] Error retirando solicitud:', errorData);
    }
  } catch (error) {
    console.error('[Assistant] Error en handleWithdrawApplication:', error);
  }
}

// ══════════════════════════════════════════════════
// ACCIÓN: INCIDENCIA (MINI-FORM)
// ══════════════════════════════════════════════════

function openIncidentForm(chatId) {
  incidentForm.value = {
    chatId: chatId || (activeStaysWithChat.value.length === 1 ? activeStaysWithChat.value[0].chatId : ''),
    type: '',
    description: '',
  };
  incidentSuccess.value = false;
  showIncidentForm.value = true;
}

function closeIncidentForm() {
  showIncidentForm.value = false;
}

async function submitIncident() {
  if (!canSubmitIncident.value || isSubmittingIncident.value) return;

  const chatId = incidentForm.value.chatId || (activeStaysWithChat.value.length === 1 ? activeStaysWithChat.value[0].chatId : '');
  if (!chatId) return;

  isSubmittingIncident.value = true;
  incidentSuccess.value = false;

  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const token = await user.getIdToken();
    const response = await fetch(`/api/chats/${chatId}/incidents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: incidentForm.value.type,
        description: incidentForm.value.description.trim(),
      }),
    });

    if (response.ok) {
      incidentSuccess.value = true;
      // Limpiar después de 2s y volver a alertas
      setTimeout(() => {
        showIncidentForm.value = false;
        incidentSuccess.value = false;
      }, 2000);
    } else {
      const errorData = await response.json().catch(() => ({}));
      console.error('Error reporting incident:', errorData);
    }
  } catch (error) {
    console.error('Error submitting incident:', error);
  } finally {
    isSubmittingIncident.value = false;
  }
}

// ══════════════════════════════════════════════════
// ACCIÓN: RECOMENDACIONES
// ══════════════════════════════════════════════════

async function loadAndShowRecommendations() {
  if (isLoadingRecommendations.value) return;
  if (recommendations.value.length > 0) {
    showRecommendations.value = true;
    return;
  }

  isLoadingRecommendations.value = true;
  showRecommendations.value = false;

  try {
    const uid = currentUser.value?.uid;
    if (!uid) return;

    const response = await fetch(`/api/colivings/recommendations?userId=${uid}&limit=3&minScore=40`);

    if (response.ok) {
      const data = await response.json();
      recommendations.value = data.recommendations || [];
      showRecommendations.value = true;
    }
  } catch (error) {
    console.error('Error loading recommendations:', error);
  } finally {
    isLoadingRecommendations.value = false;
  }
}

function goToColiving(rec) {
  emit('close');
  if (rec.scraped) {
    const listing = {
      ...rec,
      isExternal: true,
      externalSource: rec.externalSource || rec.sourceProvider,
    };
    const options = {
      userId: currentUser.value?.uid,
      source: 'assistant',
    };
    if (isAffiliatePartnerListing(listing)) {
      openAffiliatePartnerListing(listing, options);
    } else {
      openExternalColivingListing(listing, options);
    }
  } else {
    router.push(`/colivings/${rec.id}`);
  }
}

// ══════════════════════════════════════════════════
// ESTILOS POR ALERTA
// ══════════════════════════════════════════════════

function getAlertCardClass(alert) {
  if (alert.priority === 0) return 'border-red-200 bg-red-50/50 hover:border-red-300 hover:shadow-sm';
  if (alert.priority === 1) return 'border-amber-200 bg-amber-50/30 hover:border-amber-300 hover:shadow-sm';
  return 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm';
}

const colorMap = {
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-600' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

function getIconBgClass(alert) {
  return colorMap[alert.color]?.bg || 'bg-gray-100';
}

function getIconColorClass(alert) {
  return colorMap[alert.color]?.text || 'text-gray-500';
}
</script>

<style scoped>
/* Ocultar scrollbar en el carrusel horizontal */
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
